import { createRef, useEffect, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { MdAdd, MdDelete, MdEdit } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";

import PRButton from "~components/Generic/PRButton";
import PRContainer from "~components/Generic/PRContainer";
import PRTable from "~components/Generic/PRTable";
import { apiUrlBillingAndInvoicing, billSettingsCurrencyCodeOptions } from "~constants";
import DialogHelper from "~helpers/DialogHelper";
import HistoryHelper from "~helpers/HistoryHelper";
import { deleteBillSettings, getBillSettingList } from "~store/billingAndInvoicing/actions";
import { selectBillSettingList } from "~store/billingAndInvoicing/selectors";
import { selectCurrentProject } from "~store/user/selectors";

const BillingCatalogList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tableRef = createRef();
  const currentProject = useSelector(selectCurrentProject);

  const billSettingList = useSelector(selectBillSettingList);

  useEffect(() => {
    dispatch(getBillSettingList(currentProject.id));
  }, [dispatch, currentProject.id]);

  const isAllCurrenciesUsing = useMemo(() => {
    const currencyCodes = billSettingsCurrencyCodeOptions.map((item) => item.value);
    const usedCurrencyCodes = billSettingList.map((item) => item.currency_code);
    return currencyCodes.every((code) => usedCurrencyCodes.includes(code));
  }, [billSettingList]);

  const actions = useMemo(() => {
    return [
      {
        label: t("common.createNew"),
        icon: MdAdd,
        color: "success",
        // link: "/billing-and-invoicing/billing-catalog/form",
        onClick: async () => {
          if (isAllCurrenciesUsing) {
            await DialogHelper.showError(t("dashboard.billingCatalogList.allCurrencyCodeUsed"));
          } else {
            HistoryHelper.push("/billing-and-invoicing/billing-catalog/form", { scope: "dashboard" });
          }
        },
      },
    ];
  }, [t, isAllCurrenciesUsing]);

  const columns = useMemo(() => {
    const handleClickDelete = (row) => async () => {
      if (await DialogHelper.showQuestionDeleteWithFormat(row.currency_code)) {
        dispatch(deleteBillSettings(currentProject.id, row.id)).then(() => {
          tableRef.current.refresh();
        });
      }
    };
    return [
      {
        label: t("common.currency"),
        key: "currency_code",
      },
      {
        label: t("dashboard.billingCatalogList.count"),
        key: "items",
        render: (row) => {
          return row.items?.length || 0;
        },
      },
      {
        label: t("common.actions"),
        key: "actions",
        actions: true,
        render: (row) => (
          <div className="d-flex justify-content-center gap-1">
            <PRButton
              outline
              icon={MdEdit}
              link={`/billing-and-invoicing/billing-catalog/form/${row.id}/`}
              size="sm"
              tooltipText={t("common.edit")}
            />
            <PRButton
              outline
              color="danger"
              icon={MdDelete}
              size="sm"
              tooltipText={t("common.delete")}
              onClick={handleClickDelete(row)}
            />
          </div>
        ),
      },
    ];
  }, [currentProject.id, dispatch, tableRef, t]);
  return (
    <PRContainer
      actions={actions}
      name={t("layout.sidebar.billingAndInvoicing")}
      parentName={t("layout.sidebar.billingCatalog")}
    >
      <PRTable
        ref={tableRef}
        columns={columns}
        url={apiUrlBillingAndInvoicing.getBillingSettings.format(currentProject.id)}
      />
    </PRContainer>
  );
};

export default BillingCatalogList;
